import './qrCodeFormComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CommonService} from "../../services";
import {IAPIResponseType} from "../../models/api.model";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import ButtonComponent from '../core/button/ButtonComponent';
import FormikAutoCompleteDropdown from '../core/formik-auto-complete-dropdown/formikAutoCompleteDropdown';
import * as Yup from "yup";
import HeadingComponent from '../heading/headingComponent';
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";

export interface QrCodeFormComponentProps {
    onUpdateComplete?: Function;
    loggedInUser?: any;
}

const qrCodeValidationSchema = Yup.object({
    facility: Yup.mixed().required('Facility Name is required.'),
    location: Yup.mixed().required('Location Name is required.'),
    qrCodeType: Yup.mixed().required('Qr code type is required.'),
    panel: Yup.mixed().required('Panel is required.'),
    qrFlow: Yup.mixed().when('qrCodeType', {
        is: (qrCodeType: any) => (qrCodeType?.code && qrCodeType?.code.toLowerCase() === "droid"),
        then: Yup.mixed().required('Select Qr flow is required'),
    }),
    provider: Yup.mixed().required('Provider is required'),
    billingMode: Yup.mixed().required('Billing mode is required'),
});

interface qrCodeDetailsFormSchema {
    facility: any,
    location: any,
    qrCodeType: any,
    panel: any,
    qrFlow: any,
    provider: any,
    billingMode: any,
}

const qrCodeDetailsFormInitialValues: qrCodeDetailsFormSchema = {
    facility: null,
    location: null,
    qrCodeType: null,
    panel: null,
    qrFlow: null,
    provider: null,
    billingMode: null,
}

const MODULE_NAME = "QrCodeList"

const QrCodeFormComponent = (props: QrCodeFormComponentProps) => {
    const {onUpdateComplete, loggedInUser} = props;

    const [panelList, setPanelList] = useState<any[]>([]);
    const [initialValues, setInitialValues] = useState<qrCodeDetailsFormSchema>(qrCodeDetailsFormInitialValues);

    const [isPanelListLoading, setIsPanelListLoading] = useState<boolean>(false);
    const [isPanelListLoadingFailed, setIsPanelListLoadingFailed] = useState<boolean>(false);

    const [paymentModeList, setPaymentModeList] = useState<any[]>([]);

    const [isPaymentModeListLoading, setIsPaymentModeListLoading] = useState<boolean>(false);
    const [isPaymentModeListLoaded, setIsPaymentModeListLoaded] = useState<boolean>(false);
    const [isPaymentModeListLoadingFailed, setIsPaymentModeListLoadingFailed] = useState<boolean>(false);

    const [facilityList, setFacilityList] = useState<any>([]);
    const [isFacilityListLoading, setIsFacilityListLoading] = useState<boolean>(false);
    const [isFacilityListLoaded, setIsFacilityListLoaded] = useState<boolean>(false);
    const [isFacilityListLoadingFailed, setIsFacilityListLoadingFailed] = useState<boolean>(false);

    const [locationList, setLocationList] = useState<any>([]);
    const [isLocationListLoaded, setIsLocationListLoaded] = useState<boolean>(false);
    const [isLocationListLoading, setIsLocationListLoading] = useState<boolean>(false);
    const [isLocationListLoadingFailed, setIsLocationListLoadingFailed] = useState<boolean>(false);

    const [physicianList, setPhysicianList] = useState<any[]>([]);
    const [isPhysicianListLoading, setIsPhysicianListLoading] = useState<boolean>(false);
    const [isPhysicianListLoadingFailed, setIsPhysicianListLoadingFailed] = useState<boolean>(false);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const [facilityDetails, setFacilityDetails] = useState<any>(undefined);
    const [locationDetails, setLocationDetails] = useState<any>(undefined);
    const [qrSelectDisable, setQrSelectDisable] = useState<boolean>(false);
    const [billingDetailsReadOnly, setBillingDetailsReadOnly] = useState<boolean>(false);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);

    useEffect(() => {
        if (loggedInUser !== null) {
            qrCodeDetailsFormInitialValues.facility = loggedInUser.facilityDetails;
            qrCodeDetailsFormInitialValues.location = loggedInUser;
            setLocationDetails(qrCodeDetailsFormInitialValues.location);
            // qrCodeDetailsFormInitialValues.qrCodeType = {title: 'Patient Registration', code: 'patient registration'};
            // qrCodeDetailsFormInitialValues.qrFlow = {title: 'FLow2', code: 'flow2'};
        } else {
            qrCodeDetailsFormInitialValues.facility = null;
        }
    }, [setLocationDetails, loggedInUser])

    const getFacilityList = useCallback(() => {
        const payload = {isActive:true};
        setIsFacilityListLoading(true);
        setIsFacilityListLoadingFailed(false);
        setIsFacilityListLoaded(false);
        let facilityList: any = [];
        CommonService._facility.getFacilityListLite(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    facilityList = response.data;
                }
                setFacilityList(facilityList);
                setIsFacilityListLoading(false);
                setIsFacilityListLoadingFailed(false);
                setIsFacilityListLoaded(true);
            })
            .catch((error: any) => {
                setIsFacilityListLoading(false);
                setIsFacilityListLoaded(false);
                setIsFacilityListLoadingFailed(true);
            });
    }, []);

    const getLocationList = useCallback((facilityID: any) => {
        const payload = {facilityId: facilityID,isActive:true};
        setIsLocationListLoading(true);
        setIsLocationListLoadingFailed(false);
        setIsLocationListLoaded(false);
        let locationList: any = [];
        CommonService._location.getLocationListLite(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    locationList = response.data;
                }
                setLocationList(locationList);
                setIsLocationListLoading(false);
                setIsLocationListLoadingFailed(false);
                setIsLocationListLoaded(true);
            })
            .catch((error: any) => {
                setIsLocationListLoading(false);
                setIsLocationListLoaded(false);
                setIsLocationListLoadingFailed(true);
            });
    }, []);

    useEffect(() => {
        if (!loggedInUser) {
            getFacilityList();
        }
    }, [getFacilityList, loggedInUser]);

    const getPanelList = useCallback(() => {
        const payload = {locationId: locationDetails?.id,isActive:true};
        setIsPanelListLoading(true);
        setIsPanelListLoadingFailed(false);
        let panelList: any = [];
        CommonService._test.getTestListLite(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    panelList = response.data;
                }
                setPanelList(panelList);
                setIsPanelListLoading(false);
                setIsPanelListLoadingFailed(false);
            })
            .catch((error: any) => {
                setPanelList(panelList);
                setIsPanelListLoading(false);
                setIsPanelListLoadingFailed(true);
            });
    }, [locationDetails]);

    const getPaymentModeList = useCallback(() => {
        const payload = {};
        setIsPaymentModeListLoading(true);
        setIsPaymentModeListLoaded(false);
        setIsPaymentModeListLoadingFailed(false);
        let paymentModeList: any = [];
        CommonService._staticData.getBillingModes(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    paymentModeList = response.data;
                }
                setPaymentModeList(paymentModeList);
                setIsPaymentModeListLoading(false);
                setIsPaymentModeListLoaded(true);
                setIsPaymentModeListLoadingFailed(false);
            })
            .catch((error: any) => {
                setPaymentModeList(paymentModeList);
                setIsPaymentModeListLoading(false);
                setIsPaymentModeListLoaded(false);
                setIsPaymentModeListLoadingFailed(true);
            });
    }, []);

    const getPhysicianList = useCallback(() => {
        setIsPhysicianListLoading(true);
        setIsPhysicianListLoadingFailed(false);
        let physicianList: any = [];
        const payload = {}
        const locationId = locationDetails?.id;
        CommonService._location.getPhysicianList(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response && response?.data) {
                    physicianList = response.data;
                }
                setPhysicianList(physicianList);
                setIsPhysicianListLoading(false);
                setIsPhysicianListLoadingFailed(false);
            })
            .catch((error: any) => {
                setPhysicianList(physicianList);
                setIsPhysicianListLoading(false);
                setIsPhysicianListLoadingFailed(true);
            });
    }, [locationDetails]);

    const onFacilitySelection = useCallback((value: any) => {
        if (value?.id) {
            setFacilityDetails(value);
            getLocationList(value?.id);
            setLocationDetails(undefined);
        } else {
            setFacilityDetails(undefined);
            setLocationDetails(undefined);
        }
    }, [setFacilityDetails, getLocationList]);

    const onLocationSelection = useCallback((value: any) => {
        if (value?.id) {
            setLocationDetails(value);
        } else {
            setLocationDetails(undefined);
        }
    }, [setLocationDetails]);

    useEffect(() => {
        if (currentUser.facilityDetails) {
            setFacilityDetails(currentUser.facilityDetails);
            getLocationList(currentUser.facilityDetails.id);
            if (formikRef.current) {
                formikRef.current.setFieldValue('facility', currentUser.facilityDetails);
            }
        }
    }, [currentUser, formikRef,getLocationList]);

    const onQrTypeSelection = useCallback((value: any) => {
        if (value.code === 'droid') {
            setQrSelectDisable(true);
            setBillingDetailsReadOnly(true);
        } else {
            setQrSelectDisable(false);
            setBillingDetailsReadOnly(false);
        }
    }, [setQrSelectDisable, setBillingDetailsReadOnly]);

    useEffect(() => {
        if (locationDetails?.id) {
            getPhysicianList();
            getPanelList();
        }
    }, [getPanelList, getPhysicianList, getPaymentModeList, locationDetails]);

    useEffect(() => {
        getPaymentModeList();
    }, [getPaymentModeList]);

    useEffect(() => {
        if (loggedInUser) {
            getPanelList();
            getPhysicianList();
        }
    }, [loggedInUser, getPhysicianList, getPanelList]);

    const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        validateForm();
        const payload = {
            'facilityId': values.facility.id,
            'locationId': values.location.id,
            'panelId': values.panel.id,
            'physicianId': values.provider.id,
            'type': values.qrCodeType.code,
            'flow': values.qrFlow.code,
            'billingMode': values.billingMode.code,
        };
        setSubmitting(true);
        CommonService._qrcode.addQrcode(payload)
            .then((response: IAPIResponseType<any>) => {
                if (onUpdateComplete) {
                    onUpdateComplete(response.data);
                }
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                });
                setSubmitting(false);
                CommonService._alert.showToast(response?.message || 'QR code has been added', "success");
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error || 'QR adding failed', "error");
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })

    }, [onUpdateComplete]);

    return (
        <div className="qrCode-form-screen screen t-form-screen">
            <div className="t-form t-form-drawer">
                <Formik
                    validationSchema={qrCodeValidationSchema}
                    initialValues={initialValues}
                    validateOnChange={true}
                    validateOnBlur={false}
                    validateOnMount={true}
                    enableReinitialize={true}
                    onSubmit={onFormSubmit}
                    innerRef={formikRef}
                >
                    {({isSubmitting, values, isValid, touched, setFieldValue, validateForm, errors, resetForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                            console.log(values);
                        }, [validateForm, values]);
                        return (
                            <Form noValidate={true} className={"qrCode-form-holder"}>
                                <div
                                    className="insurance-form-header d-flex justify-content-space-between mrg-bottom-20 form-header">
                                    <HeadingComponent title={'Generate QR Code'}/>

                                    <div className="deselect-content horizontal-vertical-center-align">
                                        <ButtonComponent variant={"text"}
                                                         type={'button'}
                                                         disabled={initialValues === values}
                                                         handleClick={()=>{
                                                             resetForm();
                                                             setInitialValues(qrCodeDetailsFormInitialValues)
                                                         }}
                                                         color={"info"}>
                                            Clear All
                                        </ButtonComponent>
                                    </div>
                                </div>
                                <div className="t-form-controls-wrapper">
                                    {
                                        ( currentUser.roleObj.code === 'superAdmin' || currentUser.roleObj.code === 'crm')  &&
                                        <div className='qrCode-field'>
                                            <Field name={'facility'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            fullWidth={true}
                                                            label={"Facility Name"}
                                                            required={true}
                                                            data={facilityList}
                                                            valueKey={"id"}
                                                            formikField={field}
                                                            isDataLoaded={isFacilityListLoaded}
                                                            isDataLoading={isFacilityListLoading}
                                                            isDataLoadingFailed={isFacilityListLoadingFailed}
                                                            placeholder={"Select Facility Name"}
                                                            getOptionLabel={(option => (option.name) || '')}
                                                            onUpdate={(value: any) => {
                                                                onFacilitySelection(value);
                                                                setFieldValue('location', '');
                                                                setFieldValue('panel', '');
                                                                setFieldValue('provider', '');
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    }

                                    {
                                        (currentUser.roleObj.code === 'superAdmin' || currentUser.roleObj.code === 'crm' || currentUser.roleObj.code === 'facilityAdmin')&&
                                        <div className='qrCode-field'>
                                            <Field name={'location'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            fullWidth={true}
                                                            label={"Location Name"}
                                                            required={true}
                                                            multiple={false}
                                                            disabled={!facilityDetails}
                                                            data={locationList}
                                                            valueKey={"id"}
                                                            formikField={field}
                                                            isDataLoaded={isLocationListLoaded}
                                                            isDataLoading={isLocationListLoading}
                                                            isDataLoadingFailed={isLocationListLoadingFailed}
                                                            placeholder={"Select Location Name"}
                                                            getOptionLabel={(option => (option.name) || '')}
                                                            onUpdate={(value: any) => {
                                                                onLocationSelection(value);
                                                                setFieldValue('panel', '');
                                                                setFieldValue('provider', '');
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    }

                                    <div className='qrCode-field'>
                                        <Field
                                            name={'panel'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'Panel'}
                                                        formikField={field}
                                                        data={panelList}
                                                        valueKey={"id"}
                                                        required={true}
                                                        disabled={!locationDetails}
                                                        isDataLoading={isPanelListLoading}
                                                        isDataLoadingFailed={isPanelListLoadingFailed}
                                                        placeholder={"Select Panel"}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                        onUpdate={(value: any) => {
                                                        }}/>
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className='qrCode-field'>
                                        <Field name={'qrCodeType'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'QR Code Type'}
                                                        required={true}
                                                        formikField={field}
                                                        valueKey={'code'}
                                                        data={CommonService._staticData.qrCodeType}
                                                        getOptionLabel={(option => (option.title) || '')}
                                                        placeholder={"QR Code Type"}
                                                        onUpdate={(value: any) => {
                                                            onQrTypeSelection(value);
                                                            setFieldValue('billingMode', '');
                                                            setFieldValue('qrFlow', '');
                                                            if (value.code.toLowerCase() === 'droid') {
                                                                setFieldValue('billingMode', {
                                                                    title: 'Cash',
                                                                    code: 'cash'
                                                                });
                                                            }
                                                        }}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    {
                                        values?.qrCodeType?.code !== 'patientRegistration' &&
                                        <div className='qrCode-field'>
                                            <Field
                                                name={`qrFlow`}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            fullWidth={true}
                                                            label={'Select Flow'}
                                                            formikField={field}
                                                            data={CommonService._staticData.selectFlow}
                                                            valueKey={"code"}
                                                            required={true}
                                                            disabled={!qrSelectDisable}
                                                            // isDataLoading={isPanelListLoading}
                                                            // isDataLoadingFailed={isPanelListLoadingFailed}
                                                            placeholder={"Select Flow"}
                                                            getOptionLabel={(option => (option.title) || '')}
                                                            onUpdate={(value: any) => {
                                                            }}/>
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    }
                                    <div className='qrCode-field'>
                                        <Field
                                            name={`provider`}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Select Provider'}
                                                        formikField={field}
                                                        data={physicianList}
                                                        valueKey={"id"}
                                                        required={true}
                                                        disabled={!locationDetails}
                                                        isDataLoading={isPhysicianListLoading}
                                                        isDataLoadingFailed={isPhysicianListLoadingFailed}
                                                        placeholder={"Select Provider*"}
                                                        getOptionLabel={(option => (option && option?.firstName + ' ' + option?.lastName) || '')}
                                                        onUpdate={(value: any) => {
                                                        }}/>
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className='qrCode-field'>
                                        <Field
                                            name={'billingMode'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Select Billing Mode'}
                                                        formikField={field}
                                                        data={paymentModeList}
                                                        valueKey={"code"}
                                                        required={true}
                                                        readOnly={billingDetailsReadOnly}
                                                        disabled={billingDetailsReadOnly}
                                                        isDataLoading={isPaymentModeListLoading}
                                                        isDataLoadingFailed={isPaymentModeListLoadingFailed}
                                                        isDataLoaded={isPaymentModeListLoaded}
                                                        placeholder={"Select Billing Mode*"}
                                                        getOptionLabel={(option => (option.title) || '')}
                                                        onUpdate={(value: any) => {
                                                            // setPanelDetails(value);
                                                        }}/>
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className="drawer-actions mrg-top-20 form-action-section">
                                    <ButtonComponent variant={"contained"}
                                                     fullWidth={true}
                                                     type={'submit'}
                                                     isLoading={isSubmitting}
                                                     disabled={!isValid}
                                                     color={"primary"}>
                                        Generate QR Code
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
            </div>
        </div>
    )
};

export default QrCodeFormComponent;